'use client'

import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import { type Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { type CSSProperties, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../cn'

const SmartMention = Mention.extend({
  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-mention-id') ?? element.getAttribute('data-user-id'),
        renderHTML: () => ({}),
      },
      label: {
        default: null,
        parseHTML: (element) => element.textContent,
        renderHTML: () => ({}),
      },
      kind: {
        default: 'user',
        parseHTML: (element) => element.getAttribute('data-mention-kind') ?? 'user',
        renderHTML: (attributes) => ({ 'data-mention-kind': attributes['kind'] }),
      },
      entityId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-mention-id') ?? element.getAttribute('data-user-id'),
        renderHTML: (attributes) => ({ 'data-mention-id': attributes['entityId'] ?? attributes['id'] }),
      },
    }
  },
})

export interface MentionUser {
  id: string
  name: string | null
  email?: string
  image?: string | null
}

export type MentionItemKind = 'user' | 'organization' | (string & {})

export interface MentionItem {
  id: string
  kind?: MentionItemKind
  label: string
  subtitle?: string | null
  email?: string | null
  image?: string | null
}

export interface MentionRef {
  kind: MentionItemKind
  entityId: string
  userId?: string
  displayName: string
  offset: number
  length: number
}

export interface MentionValue {
  text: string
  doc: Record<string, unknown> | null
  mentions: MentionRef[]
}

export interface MentionEditorProps {
  value: MentionValue
  onChange: (value: MentionValue) => void
  onSubmit?: () => void
  users?: MentionUser[]
  items?: MentionItem[]
  onSearch?: (query: string) => MentionItem[] | Promise<MentionItem[]>
  placeholder?: string
  disabled?: boolean
  className?: string
  contentClassName?: string
  /** Submit on Enter; use Shift+Enter for newline. Default: true. */
  submitOnEnter?: boolean
}

export interface MentionEditorHandle {
  focus: () => void
  clear: () => void
  insertMentionTrigger: () => void
}

interface MentionLeafTextNode {
  attrs?: Record<string, unknown>
  type: { name: string }
}

interface MentionTextBetweenDocument {
  textBetween: (
    from: number,
    to: number,
    blockSeparator?: string,
    leafText?: (node: MentionLeafTextNode) => string,
  ) => string
}

export function mentionEditorLeafText(node: MentionLeafTextNode): string {
  if (node.type.name === 'mention') {
    return String(node.attrs?.['label'] ?? '')
  }
  if (node.type.name === 'hardBreak') {
    return '\n'
  }
  return ''
}

export function mentionTextBeforePosition(doc: MentionTextBetweenDocument, pos: number): string {
  return doc.textBetween(0, pos, '\n', mentionEditorLeafText)
}

function buildMentions(editor: Editor): MentionRef[] {
  const mentions: MentionRef[] = []
  const doc = editor.state.doc
  doc.descendants((node, pos) => {
    if (node.type.name !== 'mention') {
      return
    }
    const offset = mentionTextBeforePosition(doc, pos).length
    const displayName = String(node.attrs.label ?? '')
    const kind = String(node.attrs.kind ?? 'user') as MentionItemKind
    const entityId = String(node.attrs.entityId ?? node.attrs.id ?? '')
    mentions.push({
      kind,
      entityId,
      userId: kind === 'user' ? entityId : undefined,
      displayName,
      offset,
      length: displayName.length,
    })
  })
  return mentions
}

function readValue(editor: Editor): MentionValue {
  return {
    text: editor.getText(),
    doc: editor.getJSON(),
    mentions: buildMentions(editor),
  }
}

function isEmptyValue(value: MentionValue): boolean {
  return value.text.trim().length === 0
}

interface SuggestionState {
  items: MentionItem[]
  rect: DOMRect | null
  getRect: (() => DOMRect | null) | null
  index: number
  pick: (i: number) => void
  dismiss: () => void
}

const MENTION_POPUP_COLLISION_PADDING = 8
const MENTION_POPUP_GAP = 6
const MENTION_POPUP_MAX_HEIGHT = 280
const MENTION_POPUP_MIN_USEFUL_HEIGHT = 120
const MENTION_POPUP_MIN_WIDTH = 240

export interface MentionPopupGeometry {
  left: number
  maxHeight: number
  placement: 'bottom' | 'top'
  top: number
}

export function resolveMentionPopupGeometry(
  rect: Pick<DOMRect, 'bottom' | 'left' | 'top'>,
  viewport: { height: number; width: number },
  options: {
    collisionPadding?: number
    gap?: number
    maxHeight?: number
    minWidth?: number
    minUsefulHeight?: number
  } = {},
): MentionPopupGeometry {
  const collisionPadding = options.collisionPadding ?? MENTION_POPUP_COLLISION_PADDING
  const gap = options.gap ?? MENTION_POPUP_GAP
  const preferredMaxHeight = Math.min(
    options.maxHeight ?? MENTION_POPUP_MAX_HEIGHT,
    Math.max(0, viewport.height - collisionPadding * 2),
  )
  const minUsefulHeight = Math.min(options.minUsefulHeight ?? MENTION_POPUP_MIN_USEFUL_HEIGHT, preferredMaxHeight)
  const minWidth = options.minWidth ?? MENTION_POPUP_MIN_WIDTH
  const spaceBelow = Math.max(0, viewport.height - rect.bottom - collisionPadding - gap)
  const spaceAbove = Math.max(0, rect.top - collisionPadding - gap)
  const placement = spaceBelow < minUsefulHeight && spaceAbove > spaceBelow ? 'top' : 'bottom'
  const availableHeight = placement === 'top' ? spaceAbove : spaceBelow
  const maxHeight = Math.min(preferredMaxHeight, availableHeight)
  const maxTop = Math.max(collisionPadding, viewport.height - collisionPadding - maxHeight)
  const idealTop = placement === 'top' ? rect.top - gap - maxHeight : rect.bottom + gap
  const top = clampNumber(idealTop, collisionPadding, maxTop)
  const maxLeft = Math.max(collisionPadding, viewport.width - collisionPadding - minWidth)
  const left = clampNumber(rect.left, collisionPadding, maxLeft)

  return { left, maxHeight, placement, top }
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function readSuggestionRect(state: SuggestionState): DOMRect | null {
  return state.getRect?.() ?? state.rect
}

function SuggestionPopup({ state }: { state: SuggestionState }) {
  const [rect, setRect] = useState<DOMRect | null>(() => readSuggestionRect(state))

  useEffect(() => {
    setRect(readSuggestionRect(state))
  }, [state])

  useEffect(() => {
    const remeasure = () => {
      const nextRect = readSuggestionRect(state)
      if (!nextRect) {
        state.dismiss()
        return
      }
      setRect(nextRect)
    }

    window.addEventListener('resize', remeasure)
    window.addEventListener('scroll', remeasure, true)
    return () => {
      window.removeEventListener('resize', remeasure)
      window.removeEventListener('scroll', remeasure, true)
    }
  }, [state])

  if (!rect || state.items.length === 0) {
    return null
  }
  const geometry = resolveMentionPopupGeometry(rect, { height: window.innerHeight, width: window.innerWidth })
  const style: CSSProperties = {
    position: 'fixed',
    top: geometry.top,
    left: geometry.left,
    maxHeight: geometry.maxHeight,
  }
  const groups = groupMentionItems(state.items)
  return createPortal(
    <div
      style={style}
      data-side={geometry.placement}
      onWheel={(e) => e.stopPropagation()}
      className="z-modal min-w-[240px] max-w-[calc(100vw-16px)] overflow-y-auto overscroll-none [scrollbar-width:thin] [scrollbar-color:var(--border)_transparent] rounded-lg border border-border bg-popover py-1 text-popover-foreground shadow-lg">
      {groups.map((group) => (
        <div key={group.kind}>
          <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {mentionGroupLabel(group.kind)}
          </div>
          {group.items.map(({ item, index }) => (
            <button
              key={`${item.kind ?? 'user'}:${item.id}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                state.pick(index)
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors',
                index === state.index ? 'bg-primary/10 text-primary' : 'hover:bg-muted',
              )}>
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium">{item.label}</span>
                {item.subtitle ? <span className="truncate text-xs text-muted-foreground">{item.subtitle}</span> : null}
              </div>
            </button>
          ))}
        </div>
      ))}
    </div>,
    document.body,
  )
}

function mentionItemsFromUsers(users: MentionUser[]): MentionItem[] {
  return users.map((user) => ({
    id: user.id,
    kind: 'user',
    label: user.name ?? user.email ?? 'Unknown',
    subtitle: user.email ?? null,
    email: user.email ?? null,
    image: user.image ?? null,
  }))
}

function mentionDisplayLabel(item: MentionItem): string {
  return item.label.startsWith('@') ? item.label : `@${item.label}`
}

function mentionCommandAttrs(item: MentionItem): {
  id: string
  entityId: string
  kind: MentionItemKind
  label: string
} {
  return {
    id: item.id,
    entityId: item.id,
    kind: item.kind ?? 'user',
    label: mentionDisplayLabel(item),
  }
}

function groupMentionItems(items: MentionItem[]): Array<{
  kind: MentionItemKind
  items: Array<{ item: MentionItem; index: number }>
}> {
  const groups: Array<{ kind: MentionItemKind; items: Array<{ item: MentionItem; index: number }> }> = []
  for (const [index, item] of items.entries()) {
    const kind = item.kind ?? 'user'
    let group = groups.find((candidate) => candidate.kind === kind)
    if (!group) {
      group = { kind, items: [] }
      groups.push(group)
    }
    group.items.push({ item, index })
  }
  return groups
}

function mentionGroupLabel(kind: MentionItemKind): string {
  if (kind === 'organization') {
    return 'Organizations'
  }
  if (kind === 'user') {
    return 'Teammates'
  }
  return String(kind)
}

const MentionEditor = forwardRef<MentionEditorHandle, MentionEditorProps>(function MentionEditor(
  {
    value,
    onChange,
    onSubmit,
    users = [],
    items = [],
    onSearch,
    placeholder = 'Write a comment...',
    disabled,
    className,
    contentClassName,
    submitOnEnter = true,
  },
  ref,
) {
  const suppressUpdate = useRef(false)
  const usersRef = useRef(users)
  usersRef.current = users
  const itemsRef = useRef(items)
  itemsRef.current = items
  const onSearchRef = useRef(onSearch)
  onSearchRef.current = onSearch

  const onSubmitRef = useRef(onSubmit)
  onSubmitRef.current = onSubmit

  const [suggestion, setSuggestion] = useState<SuggestionState | null>(null)
  const suggestionRef = useRef<SuggestionState | null>(null)
  suggestionRef.current = suggestion

  const editor = useEditor({
    extensions: [
      // Editor is plain text + mentions only. Disable every formatting mark
      // and block node so typing `**foo**` or `*foo*` stays literal — markdown
      // gets rendered on display, not while authoring.
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        bold: false,
        italic: false,
        strike: false,
        code: false,
      }),
      Placeholder.configure({ placeholder }),
      SmartMention.configure({
        HTMLAttributes: { class: 'mention-chip' },
        renderText({ node }) {
          return String(node.attrs.label ?? '')
        },
        renderHTML({ options, node }) {
          const label = String(node.attrs.label ?? '')
          const kind = String(node.attrs.kind ?? 'user')
          const entityId = String(node.attrs.entityId ?? node.attrs.id ?? '')
          return [
            'span',
            {
              ...options.HTMLAttributes,
              'data-mention-kind': kind,
              'data-mention-id': entityId,
              'data-user-id': kind === 'user' ? entityId : undefined,
              'data-type': 'mention',
            },
            label,
          ]
        },
        suggestion: {
          char: '@',
          allowSpaces: false,
          items: ({ query }) => {
            const search = onSearchRef.current
            if (search) {
              return search(query)
            }
            const q = query.toLowerCase()
            const source = itemsRef.current.length > 0 ? itemsRef.current : mentionItemsFromUsers(usersRef.current)
            if (!q) {
              return source.slice(0, 8)
            }
            return source
              .filter((item) =>
                [item.label, item.subtitle ?? '', item.email ?? ''].some((part) => part.toLowerCase().includes(q)),
              )
              .slice(0, 8)
          },
          render: () => {
            let localItems: MentionItem[] = []
            let localRect: DOMRect | null = null
            let localGetRect: (() => DOMRect | null) | null = null
            let localIndex = 0
            let localCommand: ((item: MentionItem) => void) | null = null

            const pick = (i: number) => {
              const picked = localItems[i]
              if (picked && localCommand) {
                localCommand(picked)
              }
            }
            const publish = () => {
              setSuggestion({
                items: localItems,
                rect: localRect,
                getRect: localGetRect,
                index: localIndex,
                pick,
                dismiss: () => setSuggestion(null),
              })
            }

            return {
              onStart: (props) => {
                localItems = props.items as MentionItem[]
                localGetRect = () => (props.clientRect?.() as DOMRect | null) ?? null
                localRect = localGetRect()
                localCommand = (item: MentionItem) =>
                  props.command(mentionCommandAttrs(item) as Parameters<typeof props.command>[0])
                localIndex = 0
                publish()
              },
              onUpdate: (props) => {
                localItems = props.items as MentionItem[]
                localGetRect = () => (props.clientRect?.() as DOMRect | null) ?? null
                localRect = localGetRect()
                localCommand = (item: MentionItem) =>
                  props.command(mentionCommandAttrs(item) as Parameters<typeof props.command>[0])
                if (localIndex >= localItems.length) {
                  localIndex = 0
                }
                publish()
              },
              onKeyDown: (props) => {
                if (props.event.key === 'ArrowDown') {
                  if (localItems.length > 0) {
                    localIndex = (localIndex + 1) % localItems.length
                    publish()
                  }
                  return true
                }
                if (props.event.key === 'ArrowUp') {
                  if (localItems.length > 0) {
                    localIndex = (localIndex - 1 + localItems.length) % localItems.length
                    publish()
                  }
                  return true
                }
                if (props.event.key === 'Enter' || props.event.key === 'Tab') {
                  if (localItems.length > 0) {
                    pick(localIndex)
                    return true
                  }
                  return false
                }
                if (props.event.key === 'Escape') {
                  setSuggestion(null)
                  return true
                }
                return false
              },
              onExit: () => {
                setSuggestion(null)
              },
            }
          },
        },
      }),
    ],
    immediatelyRender: false,
    content: (value.doc as string | Record<string, unknown> | null) ?? '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      if (suppressUpdate.current) {
        return
      }
      onChange(readValue(ed))
    },
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (suggestionRef.current && suggestionRef.current.items.length > 0) {
          return false
        }
        if (submitOnEnter && event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          onSubmitRef.current?.()
          return true
        }
        return false
      },
      attributes: {
        class: cn(
          'text-sm px-3 py-2 min-h-[36px] focus:outline-none [&_.mention-chip]:inline-block [&_.mention-chip]:text-primary [&_.mention-chip]:bg-primary/10 [&_.mention-chip]:rounded [&_.mention-chip]:px-1 [&_.mention-chip]:py-px [&_.mention-chip]:font-medium',
          contentClassName,
        ),
      },
    },
  })

  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    clear: () => {
      if (!editor) {
        return
      }
      suppressUpdate.current = true
      editor.commands.clearContent()
      suppressUpdate.current = false
    },
    insertMentionTrigger: () => {
      editor?.commands.focus()
      editor?.commands.insertContent('@')
    },
  }))

  useEffect(() => {
    if (!editor) {
      return
    }
    if (isEmptyValue(value) && !editor.isEmpty) {
      suppressUpdate.current = true
      editor.commands.clearContent()
      suppressUpdate.current = false
    }
  }, [editor, value])

  useEffect(() => {
    if (!editor) {
      return
    }
    editor.setEditable(!disabled)
  }, [editor, disabled])

  if (!editor) {
    return null
  }

  return (
    <div
      className={
        className ?? 'flex-1 min-w-0 rounded-md border border-border focus-within:border-primary/40 bg-transparent'
      }>
      <EditorContent editor={editor} />
      {suggestion ? <SuggestionPopup state={suggestion} /> : null}
    </div>
  )
})

export { isEmptyValue as isMentionValueEmpty, MentionEditor }

// ---------------------------------------------------------------------------
// Pure mention helpers, shared by every host that persists mention ranges
// (customer notes, school-onboarding task comments). Generic over the stored
// shape so each host can carry its own `kind` union.
// ---------------------------------------------------------------------------

/** A mention as persisted alongside a body: offset/length into the trimmed body. */
export interface MentionInputRef {
  kind: MentionItemKind
  entityId: string
  offset: number
  length: number
  displayName: string
}

/**
 * Converts the editor's MentionValue into a persistable `{ body, mentions }`
 * payload: the body is trimmed and every mention offset is re-anchored to the
 * trimmed text (dropping mentions whose range no longer matches). Returns
 * null when the trimmed body is empty.
 */
export function mentionValueToInput(value: MentionValue | null): { body: string; mentions: MentionInputRef[] } | null {
  if (!value) {
    return null
  }
  const leadingTrim = value.text.length - value.text.trimStart().length
  const body = value.text.trim()
  if (!body) {
    return null
  }
  const bodyEnd = leadingTrim + body.length
  const mentions: MentionInputRef[] = []
  let previousRawEnd = leadingTrim
  for (const mention of value.mentions) {
    const entityId = mention.entityId || mention.userId || ''
    const displayName = mention.displayName
    if (!entityId || !displayName) {
      continue
    }
    const length = displayName.length
    const rawOffset = resolveMentionRawOffset(value.text, mention.offset, displayName, previousRawEnd, bodyEnd)
    if (rawOffset === null) {
      continue
    }
    mentions.push({
      kind: mention.kind,
      entityId,
      offset: rawOffset - leadingTrim,
      length,
      displayName,
    })
    previousRawEnd = rawOffset + length
  }

  return { body, mentions }
}

/**
 * Filters stored mentions down to ranges that actually match the body:
 * sorted, non-overlapping, in-bounds, and whose slice equals the display
 * name. Hosts run this on read so a stale row can\'t paint wrong ranges.
 */
export function validMentionRanges<T extends { offset: number; length: number; displayName: string }>(
  body: string,
  mentions: readonly T[],
): T[] {
  const sorted = [...mentions].sort((a, b) => a.offset - b.offset || b.length - a.length)
  const valid: T[] = []
  let previousEnd = 0
  for (const mention of sorted) {
    const end = mention.offset + mention.length
    if (
      mention.offset < previousEnd ||
      mention.offset < 0 ||
      mention.length < 1 ||
      end > body.length ||
      body.slice(mention.offset, end) !== mention.displayName
    ) {
      continue
    }
    valid.push(mention)
    previousEnd = end
  }
  return valid
}

function resolveMentionRawOffset(
  text: string,
  preferredOffset: number,
  displayName: string,
  searchStart: number,
  bodyEnd: number,
): number | null {
  if (mentionRangeMatches(text, preferredOffset, displayName, searchStart, bodyEnd)) {
    return preferredOffset
  }
  const foundOffset = text.indexOf(displayName, searchStart)
  if (mentionRangeMatches(text, foundOffset, displayName, searchStart, bodyEnd)) {
    return foundOffset
  }
  return null
}

function mentionRangeMatches(
  text: string,
  offset: number,
  displayName: string,
  searchStart: number,
  bodyEnd: number,
): boolean {
  return (
    offset >= searchStart &&
    offset + displayName.length <= bodyEnd &&
    text.slice(offset, offset + displayName.length) === displayName
  )
}
